/**
 * The voice input language as a hook: which languages this machine's
 * dictation engine accepts, which one a recording uses when none is chosen,
 * which one the user picked, and how to change it.
 *
 * The language counterpart of `use-voice-input-devices.ts`. The list is native
 * state the webview cannot observe - a language pack can be added while the
 * app runs - so it is read when the row mounts, whenever the window regains
 * focus, and when the Windows engine choice changes, since each engine has its
 * own list. There is no polling.
 */
import { useCallback, useEffect, useState } from "react";
import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";
import { useDictationSettingsStore } from "@/stores/dictation-settings-store";
import { dictationEngineArgs } from "./dictation-engine-preference";
import type { DictationEngineChoice } from "./dictation-engine-options";

export type DictationLocale = {
  /** BCP-47 tag: what a recording start carries as `locale`. */
  id: string;
  name: string;
};

export type DictationLocales = {
  /** Exactly the languages a start accepts, after Automatic for Whisper. The
   *  row names them in the app language and sorts them by those names. */
  locales: DictationLocale[];
  /** The language a start with no choice is recognised in. */
  default_id: string;
};

const NO_LOCALES: DictationLocale[] = [];

/** Whisper's "detect the language" choice. Only Whisper lists it; the platform
 *  recogniser hears a saved one as the default. */
export const AUTOMATIC_LOCALE = "auto";

const language = (tag: string) => (tag.split("-")[0] ?? tag).toLowerCase();

/**
 * The listed language a saved tag means: the same tag, else the first in the
 * same language - the rule the native start applies, so a choice made under
 * one Windows engine ("de-DE") still shows as chosen under the other ("de").
 */
function matchingLocale(
  tag: string | null,
  locales: DictationLocale[],
): DictationLocale | null {
  if (!tag) return null;
  const lower = tag.toLowerCase();
  return (
    locales.find((locale) => locale.id.toLowerCase() === lower) ??
    locales.find((locale) => language(locale.id) === language(tag)) ??
    null
  );
}

export function useDictationLocales(activeEngine?: DictationEngineChoice) {
  const savedId = useDictationSettingsStore((s) => s.locale);
  const setLocale = useDictationSettingsStore((s) => s.setLocale);
  const engine = useDictationSettingsStore((s) => s.engine);

  // Null until the first list arrives, and again whenever a list fails: the
  // row has nothing truthful to show in either case, so it stays hidden.
  const [listing, setListing] = useState<DictationLocales | null>(null);
  const [loading, setLoading] = useState(true);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!isTauri()) return;
    setListing(null);
    setLoading(true);
    let cancelled = false;
    let latest = 0;
    const load = () => {
      // Only the newest request may answer: a slow list from before a refocus
      // or an engine switch must not overwrite the one read after it.
      const request = ++latest;
      const settle = (next: DictationLocales | null) => {
        if (!cancelled && request === latest) {
          setListing(next);
          setLoading(false);
        }
      };
      invoke<DictationLocales>(
        "dictation_supported_locales",
        activeEngine ? { engine: activeEngine } : dictationEngineArgs(),
      ).then(settle, () => settle(null));
    };
    load();
    window.addEventListener("focus", load);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", load);
    };
  }, [engine, activeEngine, attempt]);

  const locales = listing?.locales ?? NO_LOCALES;
  const selectedId =
    savedId === AUTOMATIC_LOCALE &&
    !locales.some((locale) => locale.id === AUTOMATIC_LOCALE)
      ? null
      : savedId;

  const choose = useCallback(
    (id: string | null) => {
      if (id === null) {
        setLocale(null);
        return;
      }
      // Only a listed language can be chosen; anything else is what the
      // native start would refuse.
      const locale = locales.find((candidate) => candidate.id === id);
      if (locale) setLocale(locale.id);
    },
    [locales, setLocale],
  );

  return {
    /** Whether there is a list to choose from: desktop, and the list loaded. */
    available: listing !== null,
    loading,
    retry: () => setAttempt((value) => value + 1),
    locales,
    /** The language used while nothing is chosen, as the engine names it. */
    defaultId: listing?.default_id ?? null,
    /** The persisted choice, or null while the language is the default -
     *  which a saved Automatic is, to an engine that does not list it. */
    selectedId,
    /** The listed language the choice means, or null when it is the default
     *  or no longer listed (its language pack was removed). */
    selectedLocale: matchingLocale(selectedId, locales),
    choose,
    /** Whether the platform recogniser listens, whose languages are added in
     *  the system's settings; Whisper has every language built in. */
    platformEngine: (activeEngine ?? engine) !== "whisper",
  };
}
